// src/contexts/NavigationContext.js
//
// Phase 3 — Navigation Architecture: Context Switching (Step 4)
//
// Tracks the user's current *navigation context* (Personal / Church /
// Circle / Ministry / Group) for the ContextSwitcher and ContextHeader.
//
// This is intentionally separate from `DomainContext` (Phase 1's
// COMMUNITY/CHURCH trust domain). DomainContext remains the source of truth
// for trust-domain switching and `activeChurch`; NavigationContext layers
// Personal/Circle/Ministry/Group on top and stays route-aware so deep links
// into /circles/:circleId or /churches/:churchId* update the indicator
// automatically.

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useLocation } from "react-router-dom";
import { useDomain, TRUST_DOMAINS } from "./DomainContext";
import { NAV_CONTEXTS } from "../navigation/navigationDomains";

const NAV_CONTEXT_STORAGE_KEY = "lifecircle.navContext";
const ACTIVE_CIRCLE_STORAGE_KEY = "lifecircle.activeCircleId";

const NavigationContext = createContext(null);

export const useNavigation = () => useContext(NavigationContext);

function readStoredNavContext() {
  try {
    const stored = localStorage.getItem(NAV_CONTEXT_STORAGE_KEY);
    return Object.values(NAV_CONTEXTS).includes(stored)
      ? stored
      : NAV_CONTEXTS.PERSONAL;
  } catch {
    return NAV_CONTEXTS.PERSONAL;
  }
}

function readStoredCircleId() {
  try {
    return localStorage.getItem(ACTIVE_CIRCLE_STORAGE_KEY) || null;
  } catch {
    return null;
  }
}

// Matches /circles/:circleId or /groups/:groupId (but not the bare list
// routes /circles, /groups, or sub-paths handled separately).
const CIRCLE_ROUTE_RE = /^\/(?:circles|groups)\/([^/]+)/;
const CHURCH_ROUTE_RE = /^\/churches\/([^/]+)/;

export const NavigationProvider = ({ children }) => {
  const location = useLocation();
  const domain = useDomain();

  const [navContext, setNavContextState] = useState(readStoredNavContext);
  const [activeCircleId, setActiveCircleIdState] = useState(readStoredCircleId);

  const persistNavContext = useCallback((value) => {
    setNavContextState(value);
    try {
      localStorage.setItem(NAV_CONTEXT_STORAGE_KEY, value);
    } catch {
      // ignore storage failures (private browsing, etc.)
    }
  }, []);

  const persistActiveCircleId = useCallback((value) => {
    setActiveCircleIdState(value);
    try {
      if (value) {
        localStorage.setItem(ACTIVE_CIRCLE_STORAGE_KEY, value);
      } else {
        localStorage.removeItem(ACTIVE_CIRCLE_STORAGE_KEY);
      }
    } catch {
      // ignore storage failures
    }
  }, []);

  // Route-aware updates (Step 4 requirement): deep-linking into a circle or
  // church updates the current context indicator without requiring the user
  // to use the switcher first.
  useEffect(() => {
    const circleMatch = location.pathname.match(CIRCLE_ROUTE_RE);
    if (circleMatch) {
      persistNavContext(NAV_CONTEXTS.CIRCLE);
      persistActiveCircleId(circleMatch[1]);
      return;
    }

    const churchMatch = location.pathname.match(CHURCH_ROUTE_RE);
    if (churchMatch) {
      persistNavContext(NAV_CONTEXTS.CHURCH);
      if (domain && !domain.isChurch) {
        domain.switchToChurch(churchMatch[1]);
      }
      return;
    }

    // Leaving a circle/church-specific route returns to whichever
    // higher-level context is active (Church if the trust domain is CHURCH,
    // Personal otherwise). Only auto-correct away from CIRCLE — Church and
    // Personal contexts persist across general navigation.
    if (navContext === NAV_CONTEXTS.CIRCLE) {
      persistNavContext(
        domain?.domain === TRUST_DOMAINS.CHURCH
          ? NAV_CONTEXTS.CHURCH
          : NAV_CONTEXTS.PERSONAL
      );
    }
  }, [location.pathname]);

  const switchToPersonal = useCallback(() => {
    persistNavContext(NAV_CONTEXTS.PERSONAL);
    domain?.switchToCommunity();
  }, [domain, persistNavContext]);

  const switchToChurchContext = useCallback(
    (churchId) => {
      persistNavContext(NAV_CONTEXTS.CHURCH);
      domain?.switchToChurch(churchId);
    },
    [domain, persistNavContext]
  );

  const switchToCircleContext = useCallback(
    (circleId) => {
      persistNavContext(NAV_CONTEXTS.CIRCLE);
      persistActiveCircleId(circleId);
    },
    [persistNavContext, persistActiveCircleId]
  );

  const value = useMemo(
    () => ({
      navContext,
      activeCircleId,
      switchToPersonal,
      switchToChurch: switchToChurchContext,
      switchToCircle: switchToCircleContext,
      // Ministry/Group contexts are placeholders — no dedicated data model
      // until Phase 8/9. Exposed so ContextSwitcher can list them as
      // comingSoon options without throwing.
      availableContexts: NAV_CONTEXTS,
    }),
    [
      navContext,
      activeCircleId,
      switchToPersonal,
      switchToChurchContext,
      switchToCircleContext,
    ]
  );

  return (
    <NavigationContext.Provider value={value}>
      {children}
    </NavigationContext.Provider>
  );
};
